/* eslint-disable camelcase */
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { TARIFFS, Unit, UNIT_TRANSLATED } from '@enmon/commons/lib/shared';
import { Button, Card, Form, Space, Switch } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import { commodityDefaultUnit } from 'components/Form/meter_details/constants';
import { ReadingGroupSelect } from 'components/Select/ReadingGroup';
import { lensPath, set } from 'rambda';
import React, { Fragment, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputNumber } from 'components/Input/InputNumber';
import {
  CreateInventoryMeterOutputDto,
  SerializedInventoryMeterOutput,
  InventoryMeterMountType,
  isOutputTechnology,
} from '@enmon/commons/lib/tools';
import { AddonOutputSelect } from 'components/Select/MeterAddonOutput';
import { Divider } from 'components/Divider/Divider';
import { useMeterModelSuggestionsStore } from 'store/useMeterModelSuggestionsStore';
import { createDefaultRegister } from '@enmon/commons/lib/enmon';
import { FormItem } from '../../../../components/Form/FormItem';
import { ImageUpload } from '../../../../components/ImageUpload/ImageUpload';
import { Input } from '../../../../components/Input/Input';
import { Select } from '../../../../components/Select/Select';
import { FORM_LAYOUT_CONFIG } from '../../../../consts/common';
import { useMeterContext } from '../../../../contexts/Meter';
import { useScrollTopOnMount } from '../../../../hooks/useScrollTopOnMount';
import { useInventorizeStepsStore } from '../../components/store';
import { useStore } from '../../useStore';
import { ReadingStepContent as messages } from './messages';
import { ReadingStepForm } from './slice';
import { getMeterMountTypeSelectOptions } from '../utils/getMeterMountTypeOptions';
import { getMeterMountTechnologySelectOptions } from './utils/getMeterMountTechnologyOptions';
import { FullObis } from './FullObis';

export const ReadingStepContent: React.VFC = () => {
  const intl = useIntl();
  const { inventoryMeter } = useMeterContext();
  const meterId = inventoryMeter?.id;

  const [form] = Form.useForm();

  const { setNextStep } = useInventorizeStepsStore();
  const {
    newMeter,
    commodity,
    meterMountType,
    meterMountTechnology,
    remoteReadingSerial,
    requiresManualAks,
    remoteReadingOwner,
    registers,
    outputs,
    photoMeterModuleList,
    readingGroup,
    setReadingStepValues,
    setPhotoModuleList,
  } = useStore();
  const { meterModelSuggestions } = useMeterModelSuggestionsStore();
  const { inbuilt_outputs: inbuiltOutputs } = meterModelSuggestions ?? {};
  useScrollTopOnMount();

  const handleSubmit = () => {
    const values = form.getFieldsValue();
    setReadingStepValues({ readingGroup: values.readingGroup });
    setNextStep({ newMeter, commodity });
  };

  const handleUploadReadingImg = (registerIndex: number, fileList: UploadFile[]) => {
    const updatedRegisters = set(lensPath([registerIndex, 'readingPhotos']), fileList, registers ?? [{}]);

    form.setFieldsValue({ registers: updatedRegisters });
    setReadingStepValues({ registers: updatedRegisters });
  };

  // Based on selected meter model add missing outputs to the form
  useEffect(() => {
    const outputOptions = inbuiltOutputs?.selection ?? (inbuiltOutputs?.value ? [inbuiltOutputs.value] : []);
    if (outputOptions.length > 0) {
      const existingOutputs = (outputs ?? []).filter(output => 'id' in output);
      const missingMeterOutputs = outputOptions
        .filter(option => !existingOutputs.some(output => output.technology === option))
        .filter(isOutputTechnology);

      const missingOutputs = missingMeterOutputs.map(
        technology => ({ technology, is_occupied: false }) satisfies CreateInventoryMeterOutputDto,
      );

      const newOutputs = [...existingOutputs, ...missingOutputs];

      if (
        !newOutputs.every(output => outputs?.some(existingOutput => existingOutput.technology === output.technology)) ||
        newOutputs.length !== outputs?.length
      ) {
        form.setFieldsValue({
          outputs: newOutputs,
        });
        setReadingStepValues({ outputs: newOutputs });
      }
    }
  }, [inbuiltOutputs, outputs, form, setReadingStepValues]);

  const handleUploadModuleImg = (fileList: UploadFile[]) => {
    form.setFieldsValue({ photoMeterModuleList: fileList });
    setPhotoModuleList(fileList);
  };

  const handleValuesChange = (changedVals: Partial<ReadingStepForm>, allValues: ReadingStepForm) => {
    // when registers are changed update whole array so other fields are not lost
    if ('registers' in changedVals) {
      setReadingStepValues({ registers: allValues.registers, readingDate: new Date().toISOString() });
    } else if ('outputs' in changedVals) {
      allValues.outputs?.forEach(output => {
        if (output.is_occupied === false) output.remote_reading_owner = undefined;
      });
      setReadingStepValues({ outputs: allValues.outputs });
    } else if ('meterMountType' in changedVals && changedVals.meterMountType === InventoryMeterMountType.Free) {
      setReadingStepValues({
        ...changedVals,
        meterMountTechnology: undefined,
        remoteReadingOwner: undefined,
        remoteReadingSerial: undefined,
      });
    } else {
      setReadingStepValues(changedVals);
    }
  };

  return (
    <Card bodyStyle={{ padding: 0 }} bordered={false}>
      <Form
        form={form}
        id="readingStepForm"
        layout="horizontal"
        name="readingStepForm"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        {...FORM_LAYOUT_CONFIG}
      >
        <FormItem
          initialValue={requiresManualAks}
          label={<FormattedMessage {...messages.requiresManualAks} />}
          name="requiresManualAks"
          tooltip={<FormattedMessage {...messages.requiresManualAksTooltip} />}
          valuePropName="checked"
        >
          <Switch />
        </FormItem>

        <FormItem
          initialValue={readingGroup}
          label={<FormattedMessage {...messages.readingGroup} />}
          name="readingGroup"
        >
          <ReadingGroupSelect />
        </FormItem>

        {(outputs ?? []).length > 0 && (
          <>
            <Divider>
              <FormattedMessage {...messages.inbuiltOutputs} />
            </Divider>

            <Form.List initialValue={outputs} name="outputs">
              {fields =>
                fields.map((field, index) => (
                  <Fragment key={field.key}>
                    <FormItem
                      label={`${intl.formatMessage(messages.technology)} ${index + 1}`}
                      name={[field.name, 'technology' satisfies keyof SerializedInventoryMeterOutput]}
                    >
                      <AddonOutputSelect disabled />
                    </FormItem>
                    {outputs?.[field.name]?.is_occupied === true && (
                      <FormItem
                        label={<FormattedMessage {...messages.remoteReadingOwner} />}
                        name={[field.name, 'remote_reading_owner' satisfies keyof SerializedInventoryMeterOutput]}
                      >
                        <Input placeholder={intl.formatMessage(messages.remoteReadingOwnerPlaceholder)} />
                      </FormItem>
                    )}
                    <FormItem
                      label={<FormattedMessage {...messages.isOccupied} />}
                      name={[field.name, 'is_occupied' satisfies keyof SerializedInventoryMeterOutput]}
                      valuePropName="checked"
                    >
                      <Switch />
                    </FormItem>
                  </Fragment>
                ))
              }
            </Form.List>
          </>
        )}

        <>
          <Divider>
            <FormattedMessage {...messages.meterAddon} />
          </Divider>

          <FormItem
            hidden={Boolean(newMeter)}
            initialValue={meterMountType}
            label={<FormattedMessage {...messages.installationStatus} />}
            name="meterMountType"
            required={!meterId}
            rules={[
              {
                required: !meterId,
                message: intl.formatMessage(messages.installationStatusRequired),
              },
            ]}
            tooltip={<FormattedMessage {...messages.installationStatusTooltip} />}
          >
            <Select
              className="w-100"
              options={getMeterMountTypeSelectOptions(commodity, intl)}
              placeholder={<FormattedMessage {...messages.selectValid} />}
            />
          </FormItem>

          {meterMountType && meterMountType !== InventoryMeterMountType.Free && (
            <>
              <FormItem
                hidden={Boolean(newMeter)}
                initialValue={meterMountTechnology}
                label={<FormattedMessage {...messages.meterMountTechnology} />}
                name="meterMountTechnology"
                tooltip={<FormattedMessage {...messages.meterMountTechnologyTooltip} />}
              >
                <Select
                  allowClear
                  className="w-100"
                  options={getMeterMountTechnologySelectOptions(commodity, intl)}
                  placeholder={<FormattedMessage {...messages.selectValid} />}
                />
              </FormItem>

              <FormItem
                initialValue={remoteReadingOwner}
                label={<FormattedMessage {...messages.remoteReadingOwner} />}
                name="remoteReadingOwner"
                required={!meterId}
                rules={[
                  {
                    required: !meterId,
                    message: intl.formatMessage(messages.remoteReadingOwnerRequired),
                  },
                ]}
              >
                <Input placeholder={intl.formatMessage(messages.remoteReadingOwnerPlaceholder)} />
              </FormItem>

              <FormItem
                initialValue={remoteReadingSerial}
                label={<FormattedMessage {...messages.remoteReadingSerial} />}
                name="remoteReadingSerial"
                tooltip={<FormattedMessage {...messages.remoteReadingSerialTooltip} />}
              >
                <Input placeholder={intl.formatMessage(messages.remoteReadingSerialPlaceholder)} />
              </FormItem>
            </>
          )}
        </>

        {meterMountType && meterMountType !== InventoryMeterMountType.Free && (
          <FormItem
            help={<FormattedMessage {...messages.photoMeterModuleHelp} />}
            initialValue={photoMeterModuleList}
            label={<FormattedMessage {...messages.photoMeterModule} />}
            name="photoMeterModuleList"
            required={!meterId}
            rules={[
              {
                required: !meterId,
                message: intl.formatMessage(messages.photoMeterModuleRequired),
              },
            ]}
          >
            <ImageUpload
              multiple
              fileList={photoMeterModuleList}
              name="photoMeterModuleList"
              onImageUpload={handleUploadModuleImg}
            />
          </FormItem>
        )}

        <Divider>
          <FormattedMessage {...messages.registers} />
        </Divider>

        <Form.List initialValue={registers} name="registers">
          {(fields, { add, remove }) =>
            fields.map((field, index) => (
              <Fragment key={field.key}>
                <FormItem
                  label={<FormattedMessage {...messages.register} values={{ register: index + 1 }} />}
                  name={[field.name, 'name']}
                  required={!meterId}
                >
                  <Input placeholder={intl.formatMessage(messages.registerName, { register: index + 1 })} />
                </FormItem>
                <FormItem
                  label={<FormattedMessage {...messages.obisAndTariff} />}
                  name={[field.name, 'fullObis']}
                  required={!meterId}
                >
                  <FullObis />
                </FormItem>
                <FormItem
                  label={<FormattedMessage {...messages.registerStatus} />}
                  name={[field.name, 'readingCounter']}
                  required={!meterId}
                  rules={[{ required: !meterId, message: intl.formatMessage(messages.registerRequired) }]}
                >
                  <InputNumber
                    addonAfter={
                      <FormItem noStyle name={[field.name, 'unit']}>
                        <Select
                          options={[Unit.kWh, Unit.MWh, Unit.m3, Unit.GJ, Unit.MJ].map(unitOption => ({
                            value: unitOption,
                            label: UNIT_TRANSLATED[unitOption],
                          }))}
                          style={{ minWidth: 100 }}
                        />
                      </FormItem>
                    }
                    className="w-100"
                    placeholder="473.8"
                  />
                </FormItem>
                <FormItem
                  label={<FormattedMessage {...messages.registerPhotos} />}
                  name={[field.name, 'readingPhotos']}
                  required={!meterId}
                  rules={[
                    {
                      required: !meterId,
                      message: intl.formatMessage(messages.registerPhotosRequired),
                    },
                  ]}
                >
                  <ImageUpload
                    multiple
                    fileList={registers?.[field.name]?.readingPhotos ?? []}
                    name="readingPhotos"
                    onImageUpload={fileList => handleUploadReadingImg(field.name, fileList)}
                  />
                  <Space>
                    {index === fields.length - 1 && (
                      <Button
                        icon={<PlusOutlined />}
                        type="primary"
                        onClick={() => {
                          if (commodity) {
                            const { obis } = createDefaultRegister({ commodity });
                            const existingRegisterTariffs = registers
                              ?.filter(r => r.fullObis?.obis === obis)
                              .map(r => r.fullObis?.tariff ?? 0);
                            const availableTariffs = TARIFFS.filter(t => !existingRegisterTariffs?.includes(t));

                            add({
                              ...(commodity
                                ? {
                                    unit: commodityDefaultUnit[commodity],
                                    fullObis: { obis, tariff: availableTariffs[0] ?? 0 },
                                  }
                                : {}),
                            });
                          } else {
                            add({});
                          }
                        }}
                      >
                        <FormattedMessage {...messages.newRegister} />
                      </Button>
                    )}
                    <Button
                      danger
                      // only registers that does not yet exist in db can be removed
                      disabled={fields.length === 1 || !!registers?.[index]?._id}
                      icon={<CloseOutlined />}
                      type="primary"
                      onClick={() => remove(field.name)}
                    >
                      <FormattedMessage {...messages.removeRegister} />
                    </Button>
                  </Space>
                </FormItem>
              </Fragment>
            ))
          }
        </Form.List>
      </Form>
    </Card>
  );
};
